// Procedural Web Audio buffers for Dice — original synthesis, no external samples.
// Licensed for use within the AeroPlay provider platform (UNLICENSED project).

export type ProceduralSoundKind =
  | 'ui.click'
  | 'ui.select'
  | 'dice.rollPress'
  | 'dice.land'
  | 'result.lose'
  | 'result.win.small'
  | 'result.win.medium'
  | 'result.win.large';

function renderBuffer(
  ctx: AudioContext,
  durationSec: number,
  fn: (t: number, sampleRate: number) => number,
): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = Math.max(1, Math.ceil(durationSec * sampleRate));
  const buffer = ctx.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    data[i] = fn(i / sampleRate, sampleRate);
  }
  return buffer;
}

function env(t: number, attack: number, release: number, total: number): number {
  if (t < attack) return t / attack;
  if (t > total - release) return Math.max(0, (total - t) / release);
  return 1;
}

function tone(freq: number, t: number, phase = 0): number {
  return Math.sin(TWO_PI * freq * t + phase);
}

const TWO_PI = Math.PI * 2;

/** Builds one-shot procedural buffers (loops are synthesized at playback time). */
export function buildProceduralBuffer(ctx: AudioContext, kind: ProceduralSoundKind): AudioBuffer {
  switch (kind) {
    case 'ui.click':
      return renderBuffer(ctx, 0.06, (t) => tone(880, t) * env(t, 0.002, 0.04, 0.06) * 0.35);
    case 'ui.select':
      return renderBuffer(ctx, 0.08, (t) => (tone(660, t) + tone(990, t) * 0.35) * env(t, 0.003, 0.05, 0.08) * 0.28);
    case 'dice.rollPress':
      return renderBuffer(ctx, 0.14, (t) => {
        const thump = tone(120, t) * Math.exp(-t * 28);
        const click = tone(520, t) * env(t, 0.001, 0.03, 0.05) * 0.4;
        return (thump + click) * env(t, 0.002, 0.06, 0.14) * 0.55;
      });
    case 'dice.land':
      return renderBuffer(ctx, 0.18, (t) => {
        const body = tone(90, t) * Math.exp(-t * 16);
        const snap = tone(240, t) * env(t, 0.001, 0.04, 0.06) * 0.25;
        return (body + snap) * env(t, 0.002, 0.08, 0.18) * 0.5;
      });
    case 'result.lose':
      return renderBuffer(ctx, 0.35, (t) => {
        const f = 420 - t * 380;
        return tone(Math.max(80, f), t) * env(t, 0.01, 0.12, 0.35) * 0.32;
      });
    case 'result.win.small':
      return renderBuffer(ctx, 0.28, (t) => {
        const n1 = tone(523.25, t) * env(t, 0.005, 0.08, 0.14);
        const n2 = t > 0.1 ? tone(659.25, t - 0.1) * env(t - 0.1, 0.005, 0.1, 0.18) : 0;
        return (n1 + n2) * 0.38;
      });
    case 'result.win.medium':
      return renderBuffer(ctx, 0.42, (t) => {
        const n1 = tone(523.25, t) * env(t, 0.005, 0.08, 0.12);
        const n2 = t > 0.09 ? tone(659.25, t - 0.09) * env(t - 0.09, 0.005, 0.08, 0.12) : 0;
        const n3 = t > 0.18 ? tone(783.99, t - 0.18) * env(t - 0.18, 0.005, 0.14, 0.24) : 0;
        return (n1 + n2 + n3) * 0.4;
      });
    case 'result.win.large':
      return renderBuffer(ctx, 0.55, (t) => {
        const n1 = tone(392, t) * env(t, 0.005, 0.1, 0.14);
        const n2 = t > 0.08 ? tone(523.25, t - 0.08) * env(t - 0.08, 0.005, 0.1, 0.16) : 0;
        const n3 = t > 0.16 ? tone(659.25, t - 0.16) * env(t - 0.16, 0.005, 0.12, 0.2) : 0;
        const n4 = t > 0.26 ? tone(783.99, t - 0.26) * env(t - 0.26, 0.005, 0.18, 0.28) : 0;
        return (n1 + n2 + n3 + n4) * 0.42;
      });
    default:
      return renderBuffer(ctx, 0.05, () => 0);
  }
}
